import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import Header from './Header';
import { useNotifications } from '../contexts/NotificationContext';

const Layout = ({ children, currentUser, currentPage, setCurrentPage, permissions, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { toastNotifications, removeToastNotification } = useNotifications();

  // Detectar cambios de tamaño de pantalla
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024);
      // Auto-cerrar sidebar en mobile cuando se cambia de orientación
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Cerrar sidebar cuando se cambia de página en mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [currentPage, isMobile]);

  const toggleSidebar = () => {
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const getToastIcon = (type) => {
    switch (type) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      default: return 'ℹ️';
    }
  };

  const getToastColor = (type) => {
    switch (type) {
      case 'success': return 'bg-green-500';
      case 'error': return 'bg-red-500';
      case 'warning': return 'bg-yellow-500';
      default: return 'bg-blue-500';
    }
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 to-blue-50 overflow-hidden">
      {/* Sidebar - Completamente responsivo con colapso */}
      <div className={`
        fixed inset-y-0 left-0 z-50 transform transition-all duration-300 ease-in-out
        lg:relative lg:translate-x-0 lg:z-auto
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${isMobile 
          ? 'w-80 sm:w-72' 
          : sidebarCollapsed 
            ? 'w-16' 
            : 'w-64 xl:w-72 2xl:w-80'
        }
        ${isMobile ? 'shadow-2xl' : 'shadow-lg'}
      `}>
        <Sidebar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          permissions={permissions}
          onLogout={onLogout}
          currentUser={currentUser}
          isMobile={isMobile}
          isCollapsed={!isMobile && sidebarCollapsed}
          onClose={() => setSidebarOpen(false)}
        />
      </div>

      {/* Overlay para mobile - Mejorado */}
      {sidebarOpen && isMobile && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-60 backdrop-blur-sm lg:hidden transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
          aria-label="Cerrar menú"
        />
      )}

      {/* Contenido Principal - Completamente responsivo */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header - Responsivo */}
        <Header
          currentUser={currentUser}
          currentPage={currentPage}
          onToggleSidebar={toggleSidebar}
          onLogout={onLogout}
          isMobile={isMobile}
          sidebarCollapsed={sidebarCollapsed}
        />

        {/* Contenido de la página - Responsivo */}
        <main className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto">
            <div className="responsive-container responsive-padding-y min-h-full">
              <div className="fade-in">
                {children}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Sistema de Notificaciones Toast - Completamente responsivo */}
      <div className="notification-toast">
        <div className="space-y-2 sm:space-y-3">
          {toastNotifications.map((toast, index) => (
            <div
              key={toast.id}
              className={`
                ${getToastColor(toast.type)} text-white rounded-xl shadow-lg
                flex items-start space-x-3 p-4 sm:p-5
                transform transition-all duration-300 ease-in-out animate-slide-in
                backdrop-blur-sm bg-opacity-95
                ${index === 0 ? 'scale-in' : ''}
              `}
              style={{
                animationDelay: `${index * 100}ms`
              }}
            >
              <div className="flex-shrink-0 mt-0.5">
                <span className="text-lg sm:text-xl">{getToastIcon(toast.type)}</span>
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="responsive-text-sm font-medium leading-tight">
                  {toast.message}
                </p>
                {toast.description && (
                  <p className="responsive-text-xs opacity-90 mt-1">
                    {toast.description}
                  </p>
                )}
              </div>
              
              <button
                onClick={() => removeToastNotification(toast.id)}
                className="flex-shrink-0 text-white hover:text-gray-200 transition-colors duration-200 p-1 hover:bg-white hover:bg-opacity-20 rounded-full"
                aria-label="Cerrar notificación"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Indicador de carga global (opcional) */}
      {false && ( // Cambiar a true si necesitas mostrar un loader global
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 shadow-2xl">
            <div className="flex items-center space-x-3">
              <div className="spinner"></div>
              <span className="responsive-text-base font-medium text-gray-700">
                Cargando...
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Navegación inferior para móviles (opcional) */}
      {isMobile && false && ( // Cambiar a true si quieres habilitar navegación inferior
        <div className="mobile-nav">
          <div className="flex justify-around items-center py-2">
            <button
              onClick={() => setCurrentPage('dashboard')}
              className={`flex flex-col items-center space-y-1 px-3 py-2 rounded-lg transition-colors ${
                currentPage === 'dashboard' ? 'text-blue-600 bg-blue-50' : 'text-gray-600'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
              </svg>
              <span className="text-xs">Dashboard</span>
            </button>
            
            <button
              onClick={() => setCurrentPage('clients')}
              className={`flex flex-col items-center space-y-1 px-3 py-2 rounded-lg transition-colors ${
                currentPage === 'clients' ? 'text-blue-600 bg-blue-50' : 'text-gray-600'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span className="text-xs">Clientes</span>
            </button>
            
            <button
              onClick={() => setCurrentPage('allTasks')}
              className={`flex flex-col items-center space-y-1 px-3 py-2 rounded-lg transition-colors ${
                currentPage === 'allTasks' ? 'text-blue-600 bg-blue-50' : 'text-gray-600'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              <span className="text-xs">Tareas</span>
            </button>
            
            <button
              onClick={toggleSidebar}
              className="flex flex-col items-center space-y-1 px-3 py-2 rounded-lg transition-colors text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span className="text-xs">Menú</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
